/*
 * PDP Test template scripts.
 * Loaded only by the pdp-test-* sections. Variant selection itself is
 * handled by theme.js (theme.Variants) via the shared data-variant-input
 * markup — this file only reacts to its `variant:change` events for the
 * spec panels, fabric price deltas and the setup/bundle multi-item
 * add-to-basket.
 */
(function () {
  'use strict';

  if (window.pdpTestInitialised) return;
  window.pdpTestInitialised = true;

  /* "Default Title" is Shopify's placeholder variant name on a product with no
     options. It reads as real copy wherever a variant name is printed, so it is
     suppressed on the two surfaces that print one: the sticky add-to-cart bar
     and the Your Setup main line. Matched EXACTLY, not by prefix — "Default" on
     its own is a name a merchant could legitimately give a variant, and the
     Liquid these two functions mirror tests the same single string. */
  function variantLabel(title) {
    return title === 'Default Title' ? '' : title;
  }

  function formatMoney(cents, format) {
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
      return window.theme.Currency.formatMoney(cents, format).replace(/\.00(?=\D*$)/, '');
    }
    return '£' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* MIRRORS THE LIQUID IN pdp-test-fbt. The shop money format is
     "£{{amount_no_decimals}}", so formatMoney above rounds to the pound —
     which silently understates a discounted price like £84.15 as "£84". Any
     figure that can carry pence (a discounted row price, and the Your Setup
     subtotal and total that sum them) is formatted here instead: whole pounds
     read as they always did, pence appear only when they exist. Symbol taken
     from the shop format, not hardcoded. */
  function formatMoneyExact(cents, format) {
    var symbol = String(format || '£').split('{{')[0] || '£';
    /* Never render "£NaN". A bad number upstream is a bug either way, but
       unguarded it becomes a bug the CUSTOMER sees: the Your Setup panel
       showed "£NaN" on every line and on the total for anyone who changed
       variant before adding an accessory. Falling back to zero keeps the
       panel readable and keeps the fault where it belongs — in whatever
       produced the value, not in the money formatter. isFinite also catches
       Infinity and non-numeric strings, which Number() would pass through. */
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    var pounds = Math.floor(amount / 100);
    var pence = amount % 100;
    return symbol + pounds + (pence > 0 ? '.' + String(pence).padStart(2, '0') : '');
  }

  /* The sticky bar's setup total needs BOTH decimals and thousands separators,
     and nothing in the theme does both.

     theme.Currency.formatMoney has the separators and can NEVER render pence:
     theme.js:301-306 maps 'amount' and 'amount_no_decimals' alike to
     formatWithDelimiters(cents, 0), so the format string cannot rescue it.
     formatMoneyExact has the pence and builds `symbol + pounds` by hand, so it
     has no separators.

     WIDENING formatMoneyExact WOULD HAVE BEEN THE SMALLER EDIT AND IS
     DELIBERATELY NOT WHAT THIS IS. The FBT panel uses it, so adding separators
     there would silently fix the panel's own separator bug — the one where its
     total renders "£1,229" from Liquid on load and "£1229" the moment JS
     rewrites it. That is a real bug and a separate decision that has not been
     put to anyone, so formatMoneyExact keeps its behaviour to the byte and
     this stays a third function with exactly one caller: the setup branch of
     updateStickyPrice.

     PENCE ARE SHOWN ONLY WHEN NON-ZERO, so a whole-pound setup total reads
     "£500" rather than "£500.00". Three reasons, in order of weight: it is the
     rule formatMoneyExact already applies, so the button and the panel agree
     on every total rather than only on the ones that happen to have pence; the
     button's own no-accessory state is whole pounds, and switching to two
     decimals purely because an accessory was added would read as a formatting
     glitch; and the store renders whole pounds everywhere, so a trailing .00
     would appear on exactly one surface in the whole theme.

     Separator ',' and decimal '.' are hardcoded to match theme.js's own
     formatWithDelimiters defaults and the shop money format this is handed. */
  function formatSetupTotal(cents, format) {
    var symbol = String(format || '£').split('{{')[0] || '£';
    var amount = Number(cents);
    if (!isFinite(amount)) amount = 0;
    var pounds = Math.floor(amount / 100);
    var pence = amount % 100;
    var whole = String(pounds).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return symbol + whole + (pence > 0 ? '.' + String(pence).padStart(2, '0') : '');
  }

  document.addEventListener('DOMContentLoaded', function () {
    var main = document.querySelector('[data-pdp-test-main]');
    var moneyFormat = (main && main.dataset.moneyFormat) || '£{{amount}}';
    var variants = [];
    var currentVariant = null;

    if (main) {
      var variantJson = main.querySelector('[data-variant-json]');
      if (variantJson) {
        try {
          variants = JSON.parse(variantJson.textContent);
        } catch (e) {
          variants = [];
        }
      }
      var currentJson = main.querySelector('[data-current-variant-json]');
      if (currentJson) {
        try {
          currentVariant = JSON.parse(currentJson.textContent);
        } catch (e) {
          currentVariant = null;
        }
      }
    }

    /* ---- Keep the template view in the URL -------------------------- */
    /* theme.js's _updateHistoryState rebuilds the URL as
       pathname + '?variant=' + id on every variant change, dropping any
       other query parameter — including the ?view= that selects this
       template. Left alone, a reload after a variant change serves the
       default template while the A/B test still counts the visitor in
       this arm, so the view is restored after each rewrite. Wrapping
       replaceState catches the rewrite whoever makes it, rather than
       racing theme.js's own handler, and stays on replaceState so
       trying variants never adds history entries. The suffix is read
       from the rendered template, never written as a literal, so
       renaming the template cannot silently break this. */
    var templateView =
      (main && main.dataset.templateSuffix) ||
      new URLSearchParams(window.location.search).get('view') ||
      '';

    if (templateView && window.history && history.replaceState) {
      var nativeReplaceState = history.replaceState;
      var productPath = window.location.pathname;

      /* Only this product page's own URL is touched. Anything writing a
         different path — an app moving the shopper elsewhere, a filter
         or search rewrite — passes through untouched. */
      var withTemplateView = function (url) {
        var target;
        try {
          target = new URL(url || window.location.href, window.location.origin);
        } catch (e) {
          return null;
        }
        if (target.origin !== window.location.origin) return null;
        if (target.pathname !== productPath) return null;
        if (target.searchParams.get('view') === templateView) return null;
        target.searchParams.set('view', templateView);
        return target.pathname + target.search + target.hash;
      };

      history.replaceState = function (state, title, url) {
        var restored = withTemplateView(url);
        return nativeReplaceState.call(history, state, title, restored || url);
      };

      /* The URL can already have lost the view before this runs. */
      var restoredNow = withTemplateView();
      if (restoredNow) nativeReplaceState.call(history, history.state, '', restoredNow);
    }

    /* ---- Add-to-basket button price -------------------------------- */
    function updateAtcPrice(variant) {
      var el = main && main.querySelector('[data-pdp-atc-price]');
      if (!el || !variant) return;
      el.textContent = '· ' + formatMoney(variant.price, moneyFormat);
      el.classList.toggle('hide', !variant.available);
    }

    /* ---- Sticky add-to-cart bar ------------------------------------ */
    var stickyBar = main && main.querySelector('[data-pdp-sticky-atc]');
    var buyRowAtc = main && main.querySelector('.pdp-test-buyrow [data-add-to-cart]');

    function updateStickyAtc(variant) {
      if (!stickyBar) return;
      var btn = stickyBar.querySelector('[data-pdp-sticky-add]');
      var textEl = stickyBar.querySelector('[data-pdp-sticky-text]');
      var priceEl = stickyBar.querySelector('[data-pdp-sticky-price]');
      var variantEl = stickyBar.querySelector('[data-pdp-sticky-variant]');
      if (!btn || !textEl || !priceEl) return;
      var available = !!(variant && variant.available);
      btn.disabled = !available;
      textEl.textContent = available
        ? textEl.dataset.defaultText
        : textEl.dataset.soldoutText;
      priceEl.classList.toggle('hide', !available);
      if (variant && variantEl) {
        variantEl.textContent = variantLabel(variant.title);
      }
      /* Last, and the only route to the price node from here — see
         updateStickyPrice. */
      updateStickyPrice();
    }

    /* THE ONLY WRITER OF THE STICKY PRICE, and of its label while the
       variant is purchasable. Split out of updateStickyAtc so the two
       cannot fight over the same nodes.

       updateStickyAtc keeps availability — disabled, the sold-out label,
       the price span's hide class — and calls this last. updateSetupSummary
       calls it last too. On variant:change updateStickyAtc runs at position
       2 and updateSetupSummary at position 9, so this runs twice with the
       same inputs and the second write wins. Both happen in one synchronous
       task before paint, so the repeat is invisible rather than a flicker.

       SOLD OUT IS NOT THIS FUNCTION'S. updateStickyAtc has just set
       btn.disabled from variant.available, so reading it back is the
       cheapest possible way of leaving that branch exactly as it was —
       and it holds at first paint too, where nothing has run and the
       attribute is whatever the Liquid emitted.

       TWO FORMATTERS, ONE PER STATE, AND THE SPLIT IS THE POINT. The
       no-accessory state keeps theme.Currency.formatMoney, so it renders
       byte-for-byte what it has always rendered — whole pounds with
       separators. The setup state uses formatSetupTotal, which adds the pence
       formatMoney cannot produce while keeping the separators formatMoneyExact
       drops.

       What must never happen is the separator appearing and disappearing as
       the shopper toggles, so both functions emit it: "£1,229" with nothing
       selected and "£1,440.65" with an accessory. Only the pence change, and
       only because the total genuinely acquired them. */
    function updateStickyPrice() {
      if (!stickyBar) return;
      var btn = stickyBar.querySelector('[data-pdp-sticky-add]');
      var textEl = stickyBar.querySelector('[data-pdp-sticky-text]');
      var priceEl = stickyBar.querySelector('[data-pdp-sticky-price]');
      if (!btn || !textEl || !priceEl) return;
      if (btn.disabled) return;

      var items = getSelectedAccessories();

      if (!items.length) {
        textEl.textContent = textEl.dataset.defaultText;
        /* Only when there is a variant to read. On a single-option product
           [data-current-variant-json] is not rendered, so currentVariant is
           null until the first variant:change — and overwriting the
           Liquid-rendered price with a fallback would be worse than leaving
           the correct server value in place. */
        if (currentVariant && typeof currentVariant.price === 'number') {
          priceEl.textContent = '· ' + formatMoney(currentVariant.price, moneyFormat);
        }
        return;
      }

      var subtotal = items.reduce(function (sum, item) { return sum + item.price; }, 0);
      textEl.textContent = textEl.dataset.setupText || textEl.dataset.defaultText;
      priceEl.textContent = '· ' + formatSetupTotal(mainVariantPrice() + subtotal, moneyFormat);
    }

    if (stickyBar && buyRowAtc) {
      /* Not an IntersectionObserver: instant jumps (the review-stars
         anchor link, End/PageDown) can move the button from below the
         viewport to above it in a single frame — ratio stays 0, no
         crossing, no callback — leaving the bar stuck hidden (verified
         on mobile). Reading live rects on scroll is immune to that. */
      var siteFooter = document.querySelector('.site-footer');
      var stickyTicking = false;

      var refreshStickyBar = function () {
        /* Shown only once the button has been scrolled PAST (above the
           viewport) — not while it is still below the fold — and hidden
           again while the footer is on screen. */
        var passed = buyRowAtc.getBoundingClientRect().bottom < 0;
        var footerVisible = false;
        if (siteFooter) {
          var fRect = siteFooter.getBoundingClientRect();
          footerVisible = fRect.top < window.innerHeight && fRect.bottom > 0;
        }
        var show = passed && !footerVisible;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        /* Lets pdp-test.css lift the theme's floating pills and the
           OFFERS bubble clear of the bar while it is shown. */
        document.body.classList.toggle('pdp-test-sticky-bar-shown', show);
      };

      var onStickyScroll = function () {
        if (stickyTicking) return;
        stickyTicking = true;
        window.requestAnimationFrame(function () {
          stickyTicking = false;
          refreshStickyBar();
        });
      };

      window.addEventListener('scroll', onStickyScroll, { passive: true });
      window.addEventListener('resize', onStickyScroll, { passive: true });
      refreshStickyBar();

      var stickyAddBtn = stickyBar.querySelector('[data-pdp-sticky-add]');
      if (stickyAddBtn) {
        stickyAddBtn.addEventListener('click', function () {
          /* NOTHING SELECTED IS THE UNTOUCHED PATH, and it is written as the
             early return so it stays that way. The statement below is the one
             this handler has always run, not an equivalent rewrite of it: the
             empty state must keep going through the buy row's own submit
             button, theme.js's AjaxProduct and the drawer. A product whose FBT
             is hidden (pdp-test-fbt--empty) can have no selection, so it lands
             here too and one branch covers both. */
          if (getSelectedAccessories().length === 0) {
            /* Reuse the real form path (theme.js ajax cart + drawer) by
               clicking the buy row's own submit button. */
            if (!buyRowAtc.disabled) buyRowAtc.click();
            return;
          }

          /* Tent plus accessories. Deliberately NOT the form: the accessory
             inputs carry no name attribute and sit outside {% form 'product' %},
             so no amount of serialising that form could ever include them.
             Same builder and same addItems the FBT's own button uses, so the
             two payloads cannot drift.

             Errors go to the bar's own target, not the FBT panel's: whenever
             this button is on screen that panel is, by definition, not. */
          addItems(
            buildSetupItems(),
            stickyAddBtn,
            stickyBar.querySelector('[data-pdp-sticky-error]'),
            { clearsSetup: true }
          );
        });
      }
    }

    /* ---- No custom VWO event here, deliberately ---------------------
       This template used to emit VWO.event("addtocart1") on .pdp-test-atc
       clicks, mirroring the tracker in theme.liquid, so the A/B test would
       not record add-to-carts on the control arm only.

       Both arms now measure through Shopify Standard Events instead. That
       is strictly better here for two reasons. The standard
       product_added_to_cart comes from Shopify's pixel layer rather than
       theme markup, so it is arm-independent by construction and needs no
       parity code on this side. And it fires only when the cart is
       actually mutated, whereas the custom event fired on the click —
       verified by blocking the cart write and watching addtocart1 still
       reach VWO — so it counted failed adds, sold-out attempts and
       double-clicks.

       Do not reinstate it. With the standard event live, a custom event
       on this arm would report every add twice on v2 and once on the
       control, which is the exact asymmetry the original code existed to
       prevent. The control's own tracker in theme.liquid stays commented
       out for the same reason. */

    /* ---- Dynamic "Sleeps X" values --------------------------------- */
    var sleepsMap = null;
    (function () {
      var el = main && main.querySelector('[data-size-sleeps]');
      if (!el) return;
      try {
        sleepsMap = JSON.parse(el.dataset.sizeSleeps);
      } catch (e) {
        sleepsMap = null;
      }
    })();

    function updateSleeps(variant) {
      if (!sleepsMap || !variant || !main) return;
      var sizeWrap = main.querySelector('[data-pdp-fabric-selector]');
      var sizeIndex0 = sizeWrap ? parseInt(sizeWrap.dataset.sizeIndex0, 10) : 0;
      if (sizeIndex0 < 0) sizeIndex0 = 0;
      var size = variant.options[sizeIndex0];
      var sleeps = sleepsMap[size] || '';
      document.querySelectorAll('[data-pdp-sleeps-value]').forEach(function (el) {
        el.textContent = sleeps;
      });
      document.querySelectorAll('[data-pdp-sleeps-part]').forEach(function (el) {
        el.classList.toggle('hide', sleeps === '');
      });
    }

    /* ---- "Sleeps X people" heading aside (fixed size mapping) ------- */
    function updateSizePeople(variant) {
      var wrap = main && main.querySelector('[data-size-people]');
      if (!wrap || !variant) return;
      var map;
      try {
        map = JSON.parse(wrap.dataset.sizePeople);
      } catch (e) {
        return;
      }
      var fieldset = wrap.querySelector('.variant-input-wrap');
      var index = fieldset ? fieldset.dataset.index : 'option1'; // e.g. "option1"
      var text = map[variant[index]] || '';
      wrap.querySelectorAll('[data-pdp-size-people-value]').forEach(function (el) {
        el.textContent = text;
      });
      wrap.querySelectorAll('[data-pdp-size-people-part]').forEach(function (el) {
        el.classList.toggle('hide', text === '');
      });
    }

    /* ---- Size / fabric detail panels ------------------------------- */
    function updatePanels(variant) {
      if (!main || !variant) return;
      main.querySelectorAll('[data-pdp-panels]').forEach(function (group) {
        var selected = null;
        var fieldset = group.closest('.pdp-test-selector');
        fieldset = fieldset && fieldset.querySelector('.variant-input-wrap');
        if (!fieldset) return;
        var index = fieldset.dataset.index; // e.g. "option1"
        selected = variant[index];
        group.querySelectorAll('[data-pdp-panel-value]').forEach(function (panel) {
          panel.classList.toggle('hide', panel.dataset.pdpPanelValue !== selected);
        });
      });
    }

    /* ---- Fabric price deltas --------------------------------------- */
    function updateFabricDeltas(variant) {
      var wrap = main && main.querySelector('[data-pdp-fabric-selector]');
      if (!wrap || !variant || !variants.length) return;

      var sizeIndex0 = parseInt(wrap.dataset.sizeIndex0, 10);
      var fabricIndex0 = parseInt(wrap.dataset.fabricIndex0, 10);
      var currentSize = sizeIndex0 > -1 ? variant.options[sizeIndex0] : null;

      var sizeVariants = variants.filter(function (v) {
        return currentSize === null || v.options[sizeIndex0] === currentSize;
      });
      if (!sizeVariants.length) return;

      /* Deltas anchor on the selected fabric: the passed variant IS the
         current size+fabric selection, so both size changes and fabric
         changes re-anchor here. */
      var basePrice = variant.price;
      var selectedFabric = variant.options[fabricIndex0];

      /* Reversible trial setting: rows out of stock for the selected
         size hide entirely (and re-show when a size change brings them
         back) instead of rendering the Not Available delta. */
      var hideUnavailable = wrap.dataset.hideUnavailable === 'true';
      var visibleCount = 0;

      wrap.querySelectorAll('[data-pdp-fabric-delta]').forEach(function (el) {
        var fabric = el.dataset.fabricValue;
        var rowWrap = el.closest('.pdp-test-frow-wrap');
        var match = sizeVariants.filter(function (v) {
          return v.options[fabricIndex0] === fabric;
        })[0];
        var inStock = !!(match && match.available);
        if (hideUnavailable && rowWrap) {
          rowWrap.classList.toggle('hide', !inStock);
        }
        if (inStock || !hideUnavailable) visibleCount++;
        if (!match) {
          el.textContent = '';
          el.classList.remove('pdp-test-frow__delta--na');
          return;
        }
        if (!match.available) {
          el.textContent = el.dataset.unavailableLabel;
          el.classList.add('pdp-test-frow__delta--na');
          return;
        }
        el.classList.remove('pdp-test-frow__delta--na');
        if (fabric === selectedFabric) {
          el.textContent = el.dataset.includedLabel;
          return;
        }
        var delta = match.price - basePrice;
        el.textContent = (delta < 0 ? '-' : '+') +
          formatMoney(Math.abs(delta), moneyFormat);
      });

      var countEl = wrap.querySelector('[data-pdp-fabric-count]');
      if (countEl) countEl.textContent = visibleCount;
    }

    /* ---- Frequently Bought Together + Your Setup summary ------------
       One root for both halves. The accessory rows and the running-total
       aside share selection state, and every summary element is reached
       through this node, so they have to live in the same container —
       see snippets/pdp-test-fbt.liquid.

       This replaced [data-pdp-accessories] (sections/pdp-test-accessories),
       which is no longer in templates/product.v2.json. The hooks stay in the
       data-pdp-fbt-* namespace: if that section is ever re-added to a
       template by hand, the two components must not both answer to the same
       selector, or whichever lands first in the DOM silently wins. */
    var fbtSection = document.querySelector('[data-pdp-fbt]');

    /* Selected accessories live in this store, NOT in the card DOM: the
       grid re-renders per variant (below), and items the customer added
       must survive even when the new grid no longer shows their card.
       Each entry is a {id, price, title} snapshot taken at toggle time. */
    var selectedAccessories = [];

    function getSelectedAccessories() {
      return selectedAccessories.slice();
    }

    function isInSetup(id) {
      return selectedAccessories.some(function (item) { return item.id === id; });
    }

    function mainVariantPrice() {
      if (currentVariant && typeof currentVariant.price === 'number') return currentVariant.price;
      if (fbtSection) return parseInt(fbtSection.dataset.mainVariantPrice, 10) || 0;
      return 0;
    }

    function updateSetupSummary() {
      if (!fbtSection) return;
      var format = fbtSection.dataset.moneyFormat || moneyFormat;
      var items = getSelectedAccessories();
      var subtotal = items.reduce(function (sum, item) { return sum + item.price; }, 0);
      var total = mainVariantPrice() + subtotal;

      var list = fbtSection.querySelector('[data-pdp-setup-list]');
      if (list) {
        list.innerHTML = '';
        items.forEach(function (item) {
          var row = document.createElement('div');
          row.className = 'pdp-test-setup__line pdp-test-setup__line--accessory';
          var label = document.createElement('span');
          label.textContent = item.title;
          /* The trailing wrapper is kept even though it now holds only the
             price: the line's span:last-child weight rule targets it, and
             collapsing it would move that rule onto the price itself and
             change the row's flex spacing. Removal used to live in here as
             a ✕ button; it is now the accessory row's own +Add toggle. */
          var end = document.createElement('span');
          end.className = 'pdp-test-setup__line-end';
          var price = document.createElement('span');
          price.textContent = formatMoneyExact(item.price, format);
          end.appendChild(price);
          row.appendChild(label);
          row.appendChild(end);
          list.appendChild(row);
        });
      }

      var count = fbtSection.querySelector('[data-pdp-setup-count]');
      if (count) count.textContent = items.length;
      var subtotalEl = fbtSection.querySelector('[data-pdp-setup-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatMoneyExact(subtotal, format);
      var totalEl = fbtSection.querySelector('[data-pdp-setup-total]');
      if (totalEl) totalEl.textContent = formatMoneyExact(total, format);

      /* Last. The sticky button carries the same total while accessories are
         selected, so every path that can change the setup — the accessory
         toggle and variant:change — reaches the bar through this one call.
         It formats with formatMoney rather than the formatMoneyExact used
         two lines up; see updateStickyPrice for why the two differ. */
      updateStickyPrice();
    }

    /* Back to the state a page load leaves behind. Stage 6 needs this because
       a successful add no longer navigates away: before, /cart took the whole
       page with it and the selection went too. Without this the accessories
       stay ticked, the setup total still includes them and the sticky bar still
       reads "Add setup" — so a second tap silently adds them all again.

       Nothing here invents a reset. updateSetupSummary() already rebuilds the
       list, the count, the subtotal and the total from selectedAccessories, and
       its last act is updateStickyPrice(), which has its own empty branch
       restoring the bar's default label and the variant price. Emptying the
       array and un-pressing the toggles is the whole job; the existing render
       path does the rest, which is why this cannot drift from what the page
       looks like on load. */
    function clearSetupSelection() {
      if (!fbtSection) return;

      selectedAccessories.length = 0;

      fbtSection.querySelectorAll('[data-pdp-fbt-add].is-added').forEach(function (toggle) {
        toggle.classList.remove('is-added');
        var label = toggle.querySelector('[data-pdp-fbt-add-label]') || toggle;
        if (toggle.dataset.labelAdd) label.textContent = toggle.dataset.labelAdd;
      });

      updateSetupSummary();
    }

    function updateSetupMainLine(variant) {
      if (!fbtSection || !variant) return;
      var format = fbtSection.dataset.moneyFormat || moneyFormat;
      var titleEl = fbtSection.querySelector('[data-pdp-setup-variant]');
      /* The separator lives inside this span in the Liquid, so it has to live
         here too — otherwise changing variant on a stove reintroduces a bare
         "\u00b7" with nothing after it. */
      if (titleEl) {
        var vlabel = variantLabel(variant.title);
        titleEl.textContent = vlabel ? '\u00b7 ' + vlabel : '';
      }
      var priceEl = fbtSection.querySelector('[data-pdp-setup-main-price]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price, format);
    }

    if (fbtSection) {
      /* Delegated so cards created by renderAccessoryGrid need no
         rebinding. Disabled (sold out) buttons never dispatch click. */
      fbtSection.addEventListener('click', function (evt) {
        /* REMOVAL IS THE +ADD TOGGLE, SECOND PRESS. The summary rows used to
           carry their own ✕ with a separate handler branch here; both are
           gone per Fiaz. The toggle below already handled both directions —
           it splices on a second press exactly as the ✕ did — so nothing was
           left orphaned. The ✕ branch additionally had to reach back into the
           grid and un-check the card; pressing the card's own toggle updates
           it in place, so that reconciliation is no longer needed. */
        var toggle = evt.target.closest('[data-pdp-fbt-add]');
        if (!toggle) return;
        var card = toggle.closest('[data-pdp-fbt-row]');
        var input = card && card.querySelector('[data-pdp-fbt-variant]');
        if (!input) return;
        var id, price;
        if (input.tagName === 'SELECT') {
          var opt = input.options[input.selectedIndex];
          if (!opt) return;
          id = parseInt(opt.value, 10);
          price = parseInt(opt.dataset.price, 10);
        } else {
          id = parseInt(input.value, 10);
          /* The summary must total exactly what the rows show. Both read
             data-effective-price, which Liquid already truncated; falling back
             to data-price keeps pre-discount markup working unchanged.

             FALL BACK ON AN UNPARSEABLE VALUE, NOT MERELY AN ABSENT ONE.
             This was `effectivePrice || price`, which cannot fire when the
             attribute holds the STRING "undefined" — a non-empty string is
             truthy, so the guard passed it straight to parseInt and the
             summary took NaN. Parse first, then test the number. */
          price = parseInt(input.dataset.effectivePrice, 10);
          if (!isFinite(price)) price = parseInt(input.dataset.price, 10);
        }
        var index = -1;
        selectedAccessories.forEach(function (item, i) {
          if (item.id === id) index = i;
        });
        var added;
        if (index > -1) {
          selectedAccessories.splice(index, 1);
          added = false;
        } else {
          selectedAccessories.push({ id: id, price: price, title: input.dataset.title });
          added = true;
        }
        toggle.classList.toggle('is-added', added);
        var label = toggle.querySelector('[data-pdp-fbt-add-label]') || toggle;
        label.textContent = added ? toggle.dataset.labelAdded : toggle.dataset.labelAdd;
        updateSetupSummary();
      });

      /* Multi-variant accessory picker. NO LIQUID HAS EMITTED A <select> SINCE
         a60170ab (21 July), which removed the dropdowns per the mock so
         multi-variant accessories add their default first-available variant —
         the same path single-variant products already used. This handler and
         the SELECT branch in the toggle above are therefore unreachable today.

         Carried across verbatim rather than deleted: removing them is a
         behaviour change in its own right and belongs in its own commit, not
         smuggled into a layout redesign. If the picker is ever wanted back it
         is a Liquid change only. */
      fbtSection.addEventListener('change', function (evt) {
        var select = evt.target.closest('select[data-pdp-fbt-variant]');
        if (!select) return;
        var opt = select.options[select.selectedIndex];
        var priceEl = select.closest('[data-pdp-fbt-row]').querySelector('[data-pdp-fbt-price]');
        if (priceEl && opt) {
          priceEl.textContent = formatMoney(parseInt(opt.dataset.price, 10), fbtSection.dataset.moneyFormat || moneyFormat);
        }
        updateSetupSummary();
      });
    }

    /* ---- Row list live swap ----------------------------------------- */
    var fbtList = fbtSection && fbtSection.querySelector('[data-pdp-fbt-list]');
    var fbtMore = fbtSection && fbtSection.querySelector('[data-pdp-fbt-more]');
    var accessoryData = null;
    (function () {
      var el = fbtSection && fbtSection.querySelector('[data-pdp-fbt-data]');
      if (!el) return;
      try {
        accessoryData = JSON.parse(el.textContent);
      } catch (e) {
        accessoryData = null;
      }
    })();

    /* Expanded lives HERE, not in the DOM. The list is wiped and rebuilt on
       every variant change, so anything recorded on a row or on the button's
       class list is destroyed by the rebuild that follows it. A shopper who
       expands the list and then changes fabric expects it to stay expanded. */
    var fbtExpanded = false;

    function fbtVisibleCount() {
      var n = accessoryData && parseInt(accessoryData.visibleCount, 10);
      return n > 0 ? n : 3;
    }

    /* Re-applies collapse state to whatever rows are currently in the list and
       re-labels the toggle. N is recomputed from the list length every time
       because it genuinely differs per variant — 4 on Tucana, 6 on Classic
       Bell Tent — so a cached N goes wrong on the first size change. */
    function applyFbtOverflow() {
      if (!fbtList) return;
      var rows = fbtList.querySelectorAll('[data-pdp-fbt-row]');
      var visible = fbtVisibleCount();
      var hidden = Math.max(0, rows.length - visible);

      rows.forEach(function (row, i) {
        row.classList.toggle('pdp-test-fbt__row--overflow', !fbtExpanded && i >= visible);
      });

      if (!fbtMore) return;
      fbtMore.classList.toggle('pdp-test-fbt__more--hidden', hidden === 0);
      fbtMore.classList.toggle('is-expanded', fbtExpanded && hidden > 0);
      var label = fbtMore.querySelector('[data-pdp-fbt-more-label]');
      if (!label) return;
      var moreTpl = hidden === 1
        ? (fbtMore.getAttribute('data-pdp-fbt-more-one') || 'Show {count} more accessory')
        : (fbtMore.getAttribute('data-pdp-fbt-more-other') || 'Show {count} more accessories');
      label.textContent = fbtExpanded
        ? (fbtMore.getAttribute('data-pdp-fbt-less') || 'Show less')
        : moreTpl.replace('{count}', hidden);
    }

    if (fbtMore) {
      fbtMore.addEventListener('click', function () {
        fbtExpanded = !fbtExpanded;
        applyFbtOverflow();
      });
    }

    /* Matches Liquid's `| downcase | handle` closely enough for the badge
       modifier class. Both sides must agree or a rebuilt row loses its badge
       colour while keeping the badge. */
    function fbtHandleize(value) {
      return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    /* Mirrors the row markup in snippets/pdp-test-fbt.liquid element for
       element. Any field added to one has to be added to the other, or it
       appears on first paint and vanishes on the first variant change. */
    function buildFbtRow(item) {
      var format = fbtSection.dataset.moneyFormat || moneyFormat;
      var labels = (accessoryData && accessoryData.labels) || {};
      var addLabel = labels.add || '+ Add';
      var addedLabel = labels.added || 'Added';
      var added = isInSetup(item.id);

      var row = document.createElement('div');
      row.className = 'pdp-test-fbt__row' + (item.available ? '' : ' pdp-test-fbt__row--unavailable');
      row.setAttribute('data-pdp-fbt-row', '');

      var media = document.createElement('a');
      media.className = 'pdp-test-fbt__media';
      media.href = item.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (item.img) {
        var img = document.createElement('img');
        img.className = 'pdp-test-fbt__img';
        img.src = item.img;
        if (item.img2x) img.srcset = item.img + ' 1x, ' + item.img2x + ' 2x';
        img.alt = item.title;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      row.appendChild(media);

      var info = document.createElement('div');
      info.className = 'pdp-test-fbt__info';

      var name = document.createElement('a');
      name.className = 'pdp-test-fbt__name';
      name.href = item.url;
      name.textContent = item.title;
      info.appendChild(name);

      /* Badge and note are the whole reason the payload was rewritten. Both
         are optional and both omit their element entirely when unset — an
         empty pill or a blank line would be worse than nothing. */
      if (item.badge) {
        var badge = document.createElement('span');
        badge.className = 'pdp-test-fbt__badge pdp-test-fbt__badge--' + fbtHandleize(item.badge);
        badge.textContent = item.badge;
        info.appendChild(badge);
      }

      if (item.note) {
        var note = document.createElement('p');
        note.className = 'pdp-test-fbt__note';
        note.textContent = item.note;
        info.appendChild(note);
      }

      if (item.descriptor) {
        var descriptor = document.createElement('p');
        descriptor.className = 'pdp-test-fbt__descriptor';
        descriptor.textContent = item.descriptor;
        info.appendChild(descriptor);
      }

      var input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-pdp-fbt-variant', '');
      input.value = item.id;
      input.dataset.price = item.price;
      /* fullTitle, not title — Your Setup has to distinguish two sizes of the
         same accessory once both are in the list. */
      input.dataset.title = item.fullTitle || item.title;
      /* Mirrors the Liquid element for element. netPrice is already
         truncated server-side, so the two never disagree by a penny — the
         rounding decision lives in one place, not two. Older payloads without
         netPrice fall back to the plain price.

         DECLARED ABOVE ITS FIRST USE, DELIBERATELY. This used to sit twelve
         lines below `input.dataset.effectivePrice = net`, so hoisting handed
         that assignment `undefined` — silently, because var does not throw.
         dataset coerced it to the string "undefined", the summary parsed NaN
         from it and printed "£NaN" on every line and on the total.

         The row price below rendered correctly throughout, because that read
         happens after the assignment. Only rows built HERE were affected, so
         first paint was fine and it surfaced only after a variant change
         swapped the accessory set — which is why it survived review. Keep
         this declaration above the input block. */
      var net = (typeof item.netPrice === 'number') ? item.netPrice : item.price;
      input.dataset.effectivePrice = net;
      info.appendChild(input);

      row.appendChild(info);

      var actions = document.createElement('div');
      actions.className = 'pdp-test-fbt__actions';

      var priceRow = document.createElement('span');
      priceRow.className = 'pdp-test-fbt__pricerow';

      var price = document.createElement('span');
      price.className = 'pdp-test-fbt__price';
      price.setAttribute('data-pdp-fbt-price', '');
      price.textContent = formatMoneyExact(net, format);
      priceRow.appendChild(price);

      if (item.discounted && net < item.price) {
        var was = document.createElement('s');
        was.className = 'pdp-test-fbt__was';
        was.textContent = formatMoneyExact(item.price, format);
        priceRow.appendChild(was);
      }
      /* Compare-at joins the price row rather than the actions column, so all
         three sit on one line — mirrors the Liquid. */
      if (item.compareAt > item.price) {
        var compare = document.createElement('s');
        compare.className = 'pdp-test-fbt__compare';
        compare.textContent = formatMoney(item.compareAt, format);
        priceRow.appendChild(compare);
      }

      actions.appendChild(priceRow);

      if (labels.itemSave) {
        var pill = document.createElement('span');
        pill.className = 'pdp-test-pill';
        pill.textContent = labels.itemSave;
        actions.appendChild(pill);
      }

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'pdp-test-fbt__add' + (added ? ' is-added' : '');
      button.setAttribute('data-pdp-fbt-add', '');
      button.dataset.labelAdd = addLabel;
      button.dataset.labelAdded = addedLabel;
      button.disabled = !item.available;
      var buttonLabel = document.createElement('span');
      buttonLabel.setAttribute('data-pdp-fbt-add-label', '');
      buttonLabel.textContent = item.available
        ? (added ? addedLabel : addLabel)
        : (labels.soldOut || 'Sold out');
      button.appendChild(buttonLabel);
      actions.appendChild(button);

      row.appendChild(actions);

      return row;
    }

    /* Swap the list to the newly selected variant's accessories. Items already
       in the setup keep their toggles checked; everything else resets.

       The component hides itself rather than disappearing from the DOM when a
       variant has no accessories — 31 of 99 tent variants are in that state,
       and a shopper who lands on one and then switches size must get the rows
       back. A missing container could never come back. */
    function updateFbtForVariant(variant) {
      if (!fbtList || !accessoryData || !variant) return;
      var key = accessoryData.byVariant[variant.id] ? String(variant.id) : 'fallback';
      if (key === fbtList.dataset.currentSet) return;
      var items = key === 'fallback' ? accessoryData.fallback : accessoryData.byVariant[key];
      items = items || [];

      /* Sold-out accessories sort to the bottom, matching the two passes the
         Liquid does in pdp-test-fbt.liquid. This is the live-update half: if
         Liquid sorts and this does not, first paint is right and every variant
         change silently reorders the list — the #334 failure, where a fault
         appeared only after a size or fabric change and the obvious test
         passed.

         The JSON payload is deliberately left in metafield order rather than
         emitted pre-sorted, so it stays a faithful copy of what the
         merchandiser entered and there is one sort, expressed twice, rather
         than two orders to keep in step.

         slice() first: accessoryData.byVariant[key] is the shared payload
         array, and sorting in place would reorder it for every later reader.
         Array.prototype.sort is stable per spec, so merchandiser order survives
         within each group exactly as it does in the Liquid. */
      items = items.slice().sort(function (a, b) {
        return (a.available === false ? 1 : 0) - (b.available === false ? 1 : 0);
      });

      fbtList.innerHTML = '';
      items.forEach(function (item) {
        fbtList.appendChild(buildFbtRow(item));
      });
      fbtList.dataset.currentSet = key;
      if (fbtSection) fbtSection.classList.toggle('pdp-test-fbt--empty', items.length === 0);
      applyFbtOverflow();
    }

    /* ---- Multi-item add to basket ----------------------------------

       THREE SURFACES GO THROUGH HERE, and they are the whole of stage 6's PDP
       work: the FBT's "Add setup to basket", the sticky bar (#345), and the
       [data-pdp-bundle-add] buttons.

       FLAG OFF, THIS IS UNCHANGED — a successful add still leaves the page for
       /cart. That navigation exists because Rebuy owns the drawer and a JSON
       add does not open it; the note in #345 deferring this said as much.

       Flag on, the drawer is ours and ajaxProduct:added is the theme's own way
       in: theme.js:5120 runs buildCart() then open() on it, which is exactly
       what the buy row above these buttons already does. That closes #345's
       deferred question — the sticky bar and the buy row now land in the same
       place.

       The event's detail is shaped for a single-product add and this is a
       multi-item one. Checked before reusing it: NEITHER listener reads detail
       — theme.js:5120 (the drawer) and theme.js:9231 (the cart page) both take
       no argument from it — so the shape cannot matter to the theme. It is
       still passed through as faithfully as possible for anything else that
       may be listening, which is why the body is parsed at all.

       The parse is guarded so it can never turn a SUCCESSFUL add into an error
       message: a malformed body yields null and the dispatch goes ahead. */
    function nativeDrawer() {
      return !!(document.body && document.body.classList.contains('bc-native-drawer'));
    }

    /* Under the market's route root, never a bare /cart path — see cartRoute
       in bc-cart-item.js. Flag-gated, as there: with the flag off the add and
       the navigation to /cart stay exactly as live has them until Stage 8. */
    function cartRoute(path) {
      var root = nativeDrawer() ? ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') : '/';
      return root + path;
    }

    function addItems(items, button, errorEl, opts) {
      if (!items.length) return;
      var clearsSetup = !!(opts && opts.clearsSetup);
      button.disabled = true;
      button.classList.add('btn--loading');
      if (errorEl) errorEl.classList.add('hide');

      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) {
            /* STAGE 6 — keep Shopify's own words. The refusal body carries a
               `description` it wrote, localised, knowing the stock position:
               "You can only add 2 of this item to your cart". Throwing before
               reading it, which is what this did, is what left these three
               buttons showing one static apology for every possible reason.
               A body that will not parse leaves description undefined and the
               handler falls back to the copy already in the element. */
            return response.json().catch(function () { return null; }).then(function (body) {
              var refusal = new Error('Add to cart refused');
              refusal.description = body && body.description;
              throw refusal;
            });
          }

          if (!nativeDrawer()) {
            window.location.href = cartRoute('cart');
            return null;
          }

          return response.json().catch(function () { return null; });
        })
        .then(function (data) {
          if (!nativeDrawer()) return;

          /* Re-enabled because we STAY ON THE PAGE now. The old path navigated
             away, so nothing ever had to undo the loading state; leaving it
             would hand the shopper a dead button after a successful add. */
          button.disabled = false;
          button.classList.remove('btn--loading');

          /* BEFORE the clear, and it has to be: updateStickyPrice() returns
             early while the bar's button is disabled, so clearing first would
             leave the bar reading "Add setup" with the old total.

             Only for the two SETUP surfaces. The bundle buttons add their own
             fixed list and never touch selectedAccessories, so clearing there
             would throw away a selection the shopper made and had not yet
             added — which is why this is an explicit flag from the caller
             rather than something inferred here. */
          if (clearsSetup) clearSetupSelection();

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data, addToCartBtn: button }
          }));
        })
        .catch(function (error) {
          button.disabled = false;
          button.classList.remove('btn--loading');

          /* FLAG OFF, THIS IS EXACTLY WHAT IT WAS: unhide the element and
             leave the Liquid copy in it. Writing Shopify's description there
             would be better copy, and it would also be a live change to a
             surface nobody has device-tested on live, so it waits for the
             flag like everything else in this stage. */
          if (!nativeDrawer()) {
            if (errorEl) errorEl.classList.remove('hide');
            return;
          }

          var message = (error && error.description) ||
            (errorEl && errorEl.textContent && errorEl.textContent.trim()) ||
            (window.theme && theme.strings && theme.strings.cartUpdateError) ||
            'Sorry, we could not update your basket. Please try again.';

          if (errorEl) {
            errorEl.textContent = message;
            errorEl.classList.remove('hide');
          }

          /* Also into the drawer, for the case where it is already open — the
             message belongs wherever the shopper is looking, and on a phone
             that may not be the button they just pressed. */
          if (window.bcCartDrawer) {
            if (window.bcCartDrawer.refusedAdd) {
              window.bcCartDrawer.refusedAdd(message, function () {
                if (errorEl) errorEl.classList.add('hide');
              });
            } else {
              window.bcCartDrawer.showError(message);
            }
          }
        });
    }

    /* The setup payload: the selected variant at the stepper's quantity,
       then one of each selected accessory. Extracted so the FBT's own
       button and the sticky bar build the identical body — two copies of
       this would be two things to keep in step, which is how the sleeps
       mapping and the FBT sort both went wrong before. Behaviour is
       unchanged from the inline version it replaces. */
    function buildSetupItems() {
      var items = [];
      var mainSelect = document.querySelector('[data-product-select]');
      var qtyInput = main && main.querySelector('.js-qty__num');
      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (mainSelect && mainSelect.value) {
        items.push({ id: parseInt(mainSelect.value, 10), quantity: quantity });
      }
      getSelectedAccessories().forEach(function (item) {
        items.push({ id: item.id, quantity: 1 });
      });
      return items;
    }

    if (fbtSection) {
      var setupButton = fbtSection.querySelector('[data-pdp-add-setup]');
      if (setupButton) {
        setupButton.addEventListener('click', function () {
          addItems(
            buildSetupItems(),
            setupButton,
            fbtSection.querySelector('[data-pdp-setup-error]'),
            { clearsSetup: true }
          );
        });
      }
    }

    document.querySelectorAll('[data-pdp-bundle-add]').forEach(function (button) {
      button.addEventListener('click', function () {
        var ids = (button.dataset.variantIds || '')
          .split(',')
          .filter(Boolean)
          .map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; });
        var errorEl = button.closest('.pdp-test-bundle') &&
          button.closest('.pdp-test-bundle').querySelector('[data-pdp-bundle-error]');
        addItems(ids, button, errorEl);
      });
    });

    /* ---- React to variant changes ---------------------------------- */
    document.addEventListener('variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      currentVariant = variant;
      updateAtcPrice(variant);
      updateStickyAtc(variant);
      updateSleeps(variant);
      updateSizePeople(variant);
      updatePanels(variant);
      updateFabricDeltas(variant);
      updateFbtForVariant(variant);
      updateSetupMainLine(variant);
      updateSetupSummary();
    });

    /* Reconcile the collapse state against the server-rendered rows once on
       load. The Liquid already applied --overflow and wrote the label, so this
       is belt-and-braces rather than load-bearing — but it means the two
       implementations are proven to agree on every page load rather than only
       after the first variant change. */
    applyFbtOverflow();
  });

  /* ---- Discount pill: copy-to-clipboard + start/end window ----------
     Ported from the inline <script> in snippets/product-template.liquid,
     which v2 never loads. Deliberately outside the DOMContentLoaded block
     above and guarded by the SAME window.__discountBannerCopyBound flag the
     control uses: if both this file and that snippet ever run on one page,
     whichever lands first binds and the other is a no-op. Without a shared
     flag name the listener would bind twice and every click would copy —
     and toggle the "Copied!" state — twice.

     The click listener is delegated on document, so it also covers pills
     that appear after load. The 1s re-check compares Date.parse of the
     Z-suffixed data attributes against Date.now(), both UTC, so the pill
     flips at the correct global instant in each visitor's local time. */
  (function () {
    if (window.__discountBannerCopyBound) return;
    window.__discountBannerCopyBound = true;

    document.addEventListener('click', function (evt) {
      var codeButton = evt.target.closest('.product-discount-banner__code');
      if (!codeButton) return;

      evt.preventDefault();
      evt.stopPropagation();

      var code = (codeButton.getAttribute('data-discount-code') || '').trim();
      if (!code) return;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(function () {
          var banner = codeButton.closest('.product-discount-banner');
          if (!banner) return;
          banner.classList.add('product-discount-banner--copied');
          window.setTimeout(function () {
            banner.classList.remove('product-discount-banner--copied');
          }, 2000);
        }).catch(function () {
          /* ignore copy errors */
        });
      }
    });

    function evaluateDiscountBanners() {
      var now = Date.now();
      var banners = document.querySelectorAll('.js-discount-banner');
      banners.forEach(function (banner) {
        var startAttr = banner.getAttribute('data-start-gmt');
        var endAttr = banner.getAttribute('data-end-gmt');
        var show = true;

        if (startAttr) {
          var startMs = Date.parse(startAttr);
          if (!isNaN(startMs) && now < startMs) show = false;
        }
        if (endAttr) {
          var endMs = Date.parse(endAttr);
          if (!isNaN(endMs) && now >= endMs) show = false;
        }

        banner.style.display = show ? '' : 'none';
      });
    }

    function initDiscountBanners() {
      evaluateDiscountBanners();
      window.setInterval(evaluateDiscountBanners, 1000);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initDiscountBanners);
    } else {
      initDiscountBanners();
    }
  })();

  /* ======================================================================
     Stove selector — desktop drag-scrolling and right-edge fade.

     WHY THIS DUPLICATES bc-cart-fbt.js. The cart drawer FBT already has a
     proven pointer-drag with the same 8px threshold and the same pan-y
     handling. This is a deliberate copy, not an oversight. That component is
     mid-review and known to be broken in another respect (it is server-
     rendered once per page load and never recomputes on cart change), so
     extracting a shared helper now would couple this selector to a file that
     is about to be rewritten. ONCE THE CART DRAWER RE-RENDER WORK HAS LANDED,
     pull both onto one shared drag/fade helper and delete this block. Do not
     "tidy" it before then.

     WHY IT EXISTS AT ALL. The list hides its scrollbar on both engines
     (scrollbar-width and ::-webkit-scrollbar), so on desktop a mouse-only
     user had no route to the off-screen cards — no scrollbar, no arrows, and
     shift+wheel does not dependably translate to horizontal scroll. Seven
     cards at 132px overflow a ~660px buy-box column by roughly 2.4 cards.

     DESKTOP ONLY, matching the CSS. Below 769px the native touch scroller
     already reaches every card and pan-y would break it.

     ACCESSIBILITY, STATED PLAINLY. This helps pointer users. Keyboard and
     screen-reader users still reach the off-screen cards only because
     browsers scroll a focused element into view as they tab through the
     cards — Chrome additionally makes scroll containers focusable. That is
     browser behaviour we are relying on, not something this markup earns.
     Real arrows or a roving tabindex would earn it. Not done here.
     ====================================================================== */
  (function () {
    var DESKTOP = '(min-width: 769px)';
    var DRAG_THRESHOLD = 8;

    function isDesktop() {
      return window.matchMedia(DESKTOP).matches;
    }

    function initStoveSelector(viewport) {
      var list = viewport.querySelector('[data-pdp-stove-list]');
      if (!list) return;

      /* ---- fade ------------------------------------------------------ */
      function syncFade() {
        var max = list.scrollWidth - list.clientWidth;
        /* 1px tolerance: sub-pixel layout leaves a fractional remainder at
           the true end of the scroll, which would keep the fade lit. */
        var more = max > 1 && Math.round(list.scrollLeft) < max - 1;
        viewport.classList.toggle('has-overflow-right', more);
      }

      list.addEventListener('scroll', syncFade, { passive: true });
      window.addEventListener('resize', syncFade);
      /* Cards carry lazy images; the first layout can be narrower than the
         final one, so recheck once everything has settled. */
      window.addEventListener('load', syncFade);
      syncFade();

      /* ---- drag ------------------------------------------------------ */
      var dragging = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;
      var activeId = null;

      list.addEventListener('pointerdown', function (e) {
        if (!isDesktop()) return;
        /* Let the browser keep its native text-selection and middle-click
           behaviours; only a primary press starts a drag. */
        if (e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = list.scrollLeft;
        activeId = e.pointerId;
      });

      list.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== activeId) return;
        var dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          moved = true;
          list.classList.add('is-dragging');
          /* Captured only once the gesture is definitely a drag. Capturing on
             pointerdown would swallow the click that navigates the card. */
          if (list.setPointerCapture) {
            try { list.setPointerCapture(activeId); } catch (err) {}
          }
        }
        list.scrollLeft = startScroll - dx;
        syncFade();
        e.preventDefault();
      });

      function endDrag(e) {
        if (!dragging || (e && e.pointerId !== activeId)) return;
        dragging = false;
        list.classList.remove('is-dragging');
        if (activeId !== null && list.releasePointerCapture) {
          try { list.releasePointerCapture(activeId); } catch (err) {}
        }
        activeId = null;
        syncFade();
        /* `moved` is read by the capture-phase click handler below and reset
           there, not here — the click fires after pointerup. */
      }

      list.addEventListener('pointerup', endDrag);
      list.addEventListener('pointercancel', endDrag);

      /* A drag that ends over a card would otherwise navigate. Capture phase
         so this runs before the anchor's own default. */
      list.addEventListener(
        'click',
        function (e) {
          if (!moved) return;
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        },
        true
      );

      /* Cards are <a> wrapping an <img>. Without this the browser starts a
         native HTML5 image/link drag and the gesture is hijacked mid-swipe. */
      list.addEventListener('dragstart', function (e) {
        if (isDesktop()) e.preventDefault();
      });
    }

    function init() {
      var viewports = document.querySelectorAll('[data-pdp-stove-viewport]');
      Array.prototype.forEach.call(viewports, initStoveSelector);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
})();
